import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, CalendarDays, CheckCircle2, Clock3, Gauge, Image, Rocket, ShieldCheck, Sparkles, UploadCloud } from "lucide-react";
import { api } from "../lib/api";

type AutopilotOverview = {
  workflow: string[];
  counts: {
    totalDrafts: number;
    pendingApprovals: number;
    todayScheduled: number;
    draftsByStatus: Record<string, number>;
    jobsByStatus: Record<string, number>;
  };
  metrics: {
    sampleSize: number;
    impressions: number;
    engagements: number;
    clicks: number;
    engagementRate: number;
    ctr: number;
  };
  activeInsights: Array<{ id: string; kind: string; title: string; detail: string; confidence: number }>;
  campaigns: Array<{ id: string; name: string; status: string }>;
};

type Draft = {
  id: string;
  headline: string | null;
  sourceIdea: string | null;
  status: string;
  riskLevel: string;
  category: string;
  logoKey: string;
  productCode: string;
  scheduledAt: string | null;
  campaign: { id: string; name: string } | null;
  approvals: Array<{ id: string; status: string }>;
  variants: Array<{ id: string; platform: string; status: string }>;
};

function MetricCard({ title, value, hint }: { title: string; value: string | number; hint?: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
      <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function statusStyle(status: string) {
  const key = status.toLowerCase();
  if (key.includes("approved") || key.includes("published")) return "bg-green-100 text-green-700";
  if (key.includes("review") || key.includes("pending") || key.includes("scheduled")) return "bg-amber-100 text-amber-700";
  if (key.includes("rejected") || key.includes("failed")) return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
}

export default function Autopilot() {
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState<"assisted" | "trusted">("assisted");
  const [message, setMessage] = useState("");

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["autopilot-overview"],
    queryFn: () => api.get<AutopilotOverview>("/autopilot/overview"),
  });

  const { data: logos = [] } = useQuery({
    queryKey: ["autopilot-logos"],
    queryFn: () => api.get<Array<{ logoKey: string; brand: string; publicUrl: string; status: string }>>("/autopilot/logos"),
  });

  const { data: drafts = [], isLoading: loadingDrafts } = useQuery({
    queryKey: ["autopilot-drafts"],
    queryFn: () => api.get<Draft[]>("/autopilot/posts?limit=100"),
  });

  const pendingDraft = useMemo(
    () => drafts.find((draft) => draft.status === "draft" || draft.status === "in_review"),
    [drafts],
  );
  const approvedDraft = useMemo(() => drafts.find((draft) => draft.status === "approved"), [drafts]);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["autopilot-overview"] });
    qc.invalidateQueries({ queryKey: ["autopilot-drafts"] });
    qc.invalidateQueries({ queryKey: ["autopilot-logos"] });
  };

  const bootstrapLogos = useMutation({
    mutationFn: () => api.post<{ synced: Array<{ logoKey: string }>; missing: string[] }>("/autopilot/bootstrap/logos", {}),
    onSuccess: (res) => {
      setMessage(`Logo registry synced: ${res.synced.length} logos${res.missing.length ? `, missing ${res.missing.length}` : ""}.`);
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Logo sync failed."),
  });

  const planMonthly = useMutation({
    mutationFn: () =>
      api.post<{ generated: number; mode: string }>("/autopilot/plan/monthly", {
        year,
        month,
        totalPosts: 30,
        mode,
      }),
    onSuccess: (res) => {
      setMessage(`Monthly plan generated: ${res.generated} posts (${res.mode} mode).`);
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Planning failed."),
  });

  const generateDraft = useMutation({
    mutationFn: (postId: string) => api.post(`/autopilot/posts/${postId}/generate`, {}),
    onSuccess: () => {
      setMessage("Content generation complete.");
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Generation failed."),
  });

  const brandCompose = useMutation({
    mutationFn: (postId: string) => api.post(`/autopilot/posts/${postId}/brand`, {}),
    onSuccess: () => {
      setMessage("Brand composition rules applied.");
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Brand compose failed."),
  });

  const approvePost = useMutation({
    mutationFn: (postId: string) => api.post(`/autopilot/posts/${postId}/approve`, { action: "approved" }),
    onSuccess: () => {
      setMessage("Post approved.");
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Approval failed."),
  });

  const schedulePost = useMutation({
    mutationFn: (postId: string) => api.post(`/autopilot/posts/${postId}/schedule`, { mode }),
    onSuccess: () => {
      setMessage("Post scheduled.");
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Scheduling failed."),
  });

  const dispatchPublish = useMutation({
    mutationFn: () => api.post<{ queued: number; published: number; failed: number }>("/autopilot/publish/dispatch", { limit: 50 }),
    onSuccess: (res) => {
      setMessage(`Dispatch complete: queued ${res.queued}, published ${res.published}, failed ${res.failed}.`);
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Dispatch failed."),
  });

  const collectAnalytics = useMutation({
    mutationFn: () => api.post<{ snapshotsCreated: number }>("/autopilot/analytics/collect", { sinceDays: 30 }),
    onSuccess: (res) => {
      setMessage(`Analytics snapshots created: ${res.snapshotsCreated}.`);
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Analytics collection failed."),
  });

  const updateInsights = useMutation({
    mutationFn: () => api.post<{ updatedInsights: number }>("/autopilot/insights/update", { sinceDays: 90 }),
    onSuccess: (res) => {
      setMessage(`Insights updated: ${res.updatedInsights}.`);
      refreshAll();
    },
    onError: (err: any) => setMessage(err?.message || "Insight update failed."),
  });

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/80">MigraMarket AI Autopilot</p>
            <h1 className="mt-2 text-2xl font-semibold">Plan -&gt; Generate -&gt; Brand -&gt; Approve -&gt; Publish -&gt; Measure -&gt; Improve</h1>
            <p className="mt-2 text-sm text-white/90">
              Enforced with official Migra logos (MT, MH, MP, MV, MM, MI, MK), approved typography, and brand color system.
            </p>
          </div>
          <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm">
            <p className="font-medium">Workflow Status</p>
            <p className="text-white/90">{overview?.workflow?.join(" | ") || "loading..."}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <MetricCard title="Post Drafts" value={overview?.counts?.totalDrafts ?? "-"} />
        <MetricCard title="Pending Approvals" value={overview?.counts?.pendingApprovals ?? "-"} />
        <MetricCard title="Scheduled Today" value={overview?.counts?.todayScheduled ?? "-"} />
        <MetricCard title="Impressions" value={overview?.metrics?.impressions ?? "-"} />
        <MetricCard title="Engagement Rate" value={`${overview?.metrics?.engagementRate ?? 0}%`} />
        <MetricCard title="CTR" value={`${overview?.metrics?.ctr ?? 0}%`} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Run Control</h2>
            <div className="text-xs text-gray-500">Quick actions in order</div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="text-sm text-gray-600">
              Year
              <input
                value={year}
                onChange={(e) => setYear(Number(e.target.value) || now.getFullYear())}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-gray-600">
              Month
              <input
                value={month}
                onChange={(e) => setMonth(Math.min(12, Math.max(1, Number(e.target.value) || now.getMonth() + 1)))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-gray-600">
              Mode
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value === "trusted" ? "trusted" : "assisted")}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
              >
                <option value="assisted">Assisted Autopilot</option>
                <option value="trusted">Trusted Autopilot</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              onClick={() => bootstrapLogos.mutate()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ShieldCheck className="h-4 w-4" />
              Sync Official Logos
            </button>
            <button
              onClick={() => planMonthly.mutate()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <CalendarDays className="h-4 w-4" />
              Create 30 Post Plan
            </button>
            <button
              onClick={() => pendingDraft && generateDraft.mutate(pendingDraft.id)}
              disabled={!pendingDraft}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" />
              Prepare Content
            </button>
            <button
              onClick={() => pendingDraft && brandCompose.mutate(pendingDraft.id)}
              disabled={!pendingDraft}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Image className="h-4 w-4" />
              Apply Brand Rules
            </button>
            <button
              onClick={() => pendingDraft && approvePost.mutate(pendingDraft.id)}
              disabled={!pendingDraft}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CheckCircle2 className="h-4 w-4" />
              Approve for Publish
            </button>
            <button
              onClick={() => approvedDraft && schedulePost.mutate(approvedDraft.id)}
              disabled={!approvedDraft}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Clock3 className="h-4 w-4" />
              Queue for Publishing
            </button>
            <button
              onClick={() => dispatchPublish.mutate()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              <UploadCloud className="h-4 w-4" />
              Publish Queue Now
            </button>
            <button
              onClick={() => collectAnalytics.mutate()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Gauge className="h-4 w-4" />
              Refresh Metrics
            </button>
            <button
              onClick={() => updateInsights.mutate()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:col-span-2"
            >
              <Bot className="h-4 w-4" />
              Improve with Insights
            </button>
          </div>

          {message && <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{message}</p>}
        </section>

        <section className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Official Logo Registry</h2>
            <span className="text-xs text-gray-500">{logos.length} logos</span>
          </div>

          <div className="space-y-2">
            {logos.map((logo) => (
              <div key={logo.logoKey} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
                <div className="flex items-center gap-3">
                  <img src={logo.publicUrl} alt={logo.logoKey} className="h-8 w-8 rounded bg-white object-contain p-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{logo.logoKey} - {logo.brand}</p>
                    <p className="text-xs text-gray-500">{logo.publicUrl}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs ${statusStyle(logo.status)}`}>{logo.status}</span>
              </div>
            ))}
            {!logos.length && <p className="text-sm text-gray-500">No logos registered yet. Run bootstrap.</p>}
          </div>
        </section>
      </div>

      <section className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Autopilot Draft Queue</h2>
          <div className="text-xs text-gray-500">{loadingDrafts ? "Loading..." : `${drafts.length} drafts`}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Headline</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Schedule</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => (
                <tr key={draft.id} className="border-b border-gray-50">
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900">{draft.headline || draft.sourceIdea || "Untitled"}</p>
                    <p className="text-xs text-gray-500">{draft.campaign?.name || "No campaign"}</p>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{draft.category}</td>
                  <td className="px-3 py-2 text-gray-700">{draft.productCode}/{draft.logoKey}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${statusStyle(draft.status)}`}>{draft.status}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{draft.scheduledAt ? new Date(draft.scheduledAt).toLocaleString() : "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => generateDraft.mutate(draft.id)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        Prepare
                      </button>
                      <button
                        onClick={() => brandCompose.mutate(draft.id)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        Brand Rules
                      </button>
                      <button
                        onClick={() => approvePost.mutate(draft.id)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => schedulePost.mutate(draft.id)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        Queue
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Performance Insights</h2>
          <Rocket className="h-4 w-4 text-purple-500" />
        </div>

        <div className="space-y-3">
          {overview?.activeInsights?.map((insight) => (
            <div key={insight.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">{insight.title}</p>
                <span className="text-xs text-gray-500">{Math.round(insight.confidence * 100)}%</span>
              </div>
              <p className="text-sm text-gray-700">{insight.detail}</p>
            </div>
          ))}
          {!overview?.activeInsights?.length && !loadingOverview && (
            <p className="text-sm text-gray-500">No insights yet. Run "Collect Analytics" and "Update Performance Insights".</p>
          )}
        </div>
      </section>
    </div>
  );
}
